import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  LinearProgress,
  Button,
} from "@mui/material";
import EmojiIcon from "../Components/common/EmojiIcon";
import ai from "../Components/animations/ai.json";
import GlowBorderWrapper from "../Components/common/GlowBorderWrapper";

const RelevanceTimelineSection = () => {
  // Exact skill data from the image
  const skills = [
    {
      name: "Artificial Intelligence",
      description: "Skill trending and in demand until 2036",
      percentage: 92,
      color: "#4CAF50",
      status: "trending",
    },
    {
      name: "Design Thinking",
      description: "Skill current and in demand until 2025",
      percentage: 70,
      color: "#FF9800",
      status: "current",
    },
    {
      name: "Manual Testing",
      description: "Skill getting outdated fast 2021",
      percentage: 62,
      color: "#F44336",
      status: "outdated",
    },
    {
      name: "Typewriting",
      description: "Skill obsolete 2020",
      percentage: 60,
      color: "#2196F3",
      status: "obsolete",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        py: { xs: 4, sm: 6, md: 8 },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container maxWidth="xl">
        <Box textAlign="center">
          {/* Main Question */}
          <Typography
            variant="h2"
            sx={{
              color: "#fff",
              fontFamily: "'SF Pro Display', 'Inter', sans-serif",
              fontWeight: 600,
              fontStyle: "bold",
              fontSize: { xs: "28px", sm: "36px", md: "44px", lg: "56px" },
              lineHeight: { xs: "35px", sm: "45px", md: "55px", lg: "70px" },
              textAlign: "center",
              mb: { xs: 4, md: 6 },
              textShadow: "0px 4px 64px #FFC218",
            }}
          >
            How long you will remain relevant?
          </Typography>

          {/* Timeline and Skill Score Container */}
          <Grid
            container
            spacing={{ xs: 2, md: 4 }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left Side - Timeline */}
            <Grid item xs={12} lg={6}>
              {/* Hourglass Container */}
              <Box
                sx={{
                  backgroundColor: "#111",
                  borderRadius: "20px",
                  border: "1px solid #333",
                  padding: {
                    xs: "30px 20px",
                    sm: "40px 30px",
                    md: "60px 45px",
                  },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                {/* Hourglass Icon */}
                <Box
                  sx={{
                    fontSize: {
                      xs: "100px",
                      sm: "120px",
                      md: "140px",
                      lg: "167px",
                    },
                    color: "#fff",
                    mb: 2,
                  }}
                >
                  ⏳
                </Box>

                {/* Timeline Content */}
                <Box
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {/* Top Skill Validity Until */}
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#fff",
                      fontFamily:
                        "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      fontWeight: 500,
                      fontSize: {
                        xs: "24px",
                        sm: "32px",
                        md: "40px",
                        lg: "50px",
                      },
                      lineHeight: {
                        xs: "28px",
                        sm: "38px",
                        md: "48px",
                        lg: "60px",
                      },
                      letterSpacing: "0.5px",
                    }}
                  >
                    Top Skill Validity Until
                  </Typography>

                  {/* Year 2028 */}
                  <Typography
                    variant="h1"
                    sx={{
                      color: "#FFD700",
                      fontFamily:
                        "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      fontWeight: 600,
                      fontStyle: "bold",
                      fontSize: {
                        xs: "40px",
                        sm: "48px",
                        md: "60px",
                        lg: "80px",
                      },
                      lineHeight: {
                        xs: "44px",
                        sm: "52px",
                        md: "60px",
                        lg: "70px",
                      },
                      letterSpacing: "0.5px",
                      textAlign: "center",
                    }}
                  >
                    2028
                  </Typography>

                  {/* Trending Status */}
                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "18px", sm: "22px", md: "28px" },
                      lineHeight: { xs: "24px", sm: "30px", md: "34px" },
                    }}
                  >
                    <span
                      style={{
                        fontFamily:
                          "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontWeight: 600,
                        fontStyle: "bold",
                        fontSize: { xs: "18px", sm: "22px", md: "28px" },
                        lineHeight: { xs: "24px", sm: "30px", md: "34px" },
                        color: "#FFD700",
                      }}
                    >
                      Trending:
                    </span>{" "}
                    <span
                      style={{
                        fontFamily:
                          "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontWeight: 500,
                        fontStyle: "normal",
                        fontSize: { xs: "18px", sm: "22px", md: "28px" },
                        lineHeight: { xs: "24px", sm: "30px", md: "34px" },
                        color: "#fff",
                      }}
                    >
                      You are in demand, but keep upgrading
                    </span>
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Skill Score */}
            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  backgroundColor: "#111",
                  borderRadius: "20px",
                  border: "1px solid #333",
                  padding: "40px 30px",
                  //   height: "100%",
                  //   minHeight: "600px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 4,
                    justifyContent: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/assets/images/skill-score-relevance-01.svg"
                      width="100%"
                      height="auto"
                      alt="skill-score"
                      loading="lazy"
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily:
                        "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      fontWeight: 700,
                      fontSize: "28px",
                      color: "#fff",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Skill score
                  </Typography>
                </Box>

                {/* Skills List */}
                {skills.map((skill, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: { xs: 2, lg: 2, xl: 6 },
                      alignItems: {
                        xs: "center",
                        sm: "flex-end",
                        md: "center",
                        lg: "flex-end",
                        xl: "flex-end",
                      },
                      justifyContent: "space-between",
                      my: "15px",
                    }}
                    flexDirection={{
                      xs: "column",
                      sm: "row",
                      md: "column",
                      lg: "row",
                      xl: "row",
                    }}
                  >
                    {/* Left Side - Skill Info */}
                    <Box
                      flexGrow={1}
                      sx={{
                        width: "100%",
                        maxWidth: "700px",
                      }}
                    >
                      {/* Skill Name */}
                      <Typography
                        sx={{
                          fontFamily:
                            "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          fontSize: "22px",
                          fontWeight: 700,
                          display: "block",
                          textAlign: "left",
                          mb: "6px",
                          mr: "20px",
                        }}
                      >
                        {skill.name}
                      </Typography>

                      {/* Skill Description and Percentage Row */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "18px",
                            color: "#fff",
                            textAlign: "left",
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          }}
                        >
                          {skill.description}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "24px",
                            fontWeight: 700,
                            color: skill.color,
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          }}
                        >
                          {skill.percentage}%
                        </Typography>
                      </Box>

                      {/* Progress Bar */}
                      <Box sx={{ width: "100%" }}>
                        <LinearProgress
                          sx={{
                            height: "10px",
                            borderRadius: "20px",
                            backgroundColor: `${skill.color}20`,
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: skill.color,
                              borderRadius: "20px",
                            },
                          }}
                          variant="determinate"
                          value={skill.percentage}
                        />
                      </Box>
                    </Box>

                    {/* Right Side - Ask AI Button */}
                    <GlowBorderWrapper>
                      <Button
                        disabled={true}
                        sx={{
                          width: "200px",
                          // maxWidth: "200px",
                          color: "#fff",
                          alignItems: "center",
                          border: "1px solid rgba(74,61,158,1)",
                          justifyContent: "space-evenly",
                          padding: "7px 12px",
                          borderRadius: "27px",
                          background:
                            "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1))",
                          transition: "all 0.3s linear",
                          cursor: "default",
                          "&:disabled": {
                            background:
                              "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1))",
                            color: "#fff",
                            cursor: "default",
                            opacity: 1,
                          },
                          "&:hover": {
                            background:
                              "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1))",
                            transform: "none",
                            boxShadow: "none",
                          },
                        }}
                        className="btn-anim"
                        startIcon={
                          <Box>
                            <EmojiIcon lottieFiles={[ai]} interval={4000} />
                          </Box>
                        }
                        endIcon={
                          <Box>
                            <EmojiIcon lottieFiles={[ai]} interval={4000} />
                          </Box>
                        }
                      >
                        ASK AI
                      </Button>
                    </GlowBorderWrapper>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default RelevanceTimelineSection;
